import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { HelpCircle, MessageCircle, DollarSign, Coins, Send, Crown, Lock, Flame, Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

// Room configurations with themes
const ASK_ROOM_CONFIGS = {
  "ask-wildest-fantasy": {
    name: "Ask Me About My Wildest Fantasy",
    icon: Flame,
    theme: "from-pink-600 to-red-600",
    bgTheme: "from-pink-900/20 to-red-900/20",
    description: "Ask about my deepest fantasies...",
    entryFee: 50,
    questionFee: 25
  },
  "ask-jade-room": {
    name: "Ask Jade",
    icon: Sparkles,
    theme: "from-emerald-600 to-teal-600",
    bgTheme: "from-emerald-900/20 to-teal-900/20",
    description: "Jade will answer your burning questions...",
    entryFee: 50,
    questionFee: 25
  },
  "ask-your-master": {
    name: "Ask Your Master",
    icon: Crown,
    theme: "from-purple-600 to-indigo-600",
    bgTheme: "from-purple-900/20 to-indigo-900/20",
    description: "Submit to the Master and ask your questions...",
    entryFee: 50,
    questionFee: 25
  },
  "mother-may-i-room": {
    name: "Mother May I",
    icon: Heart,
    theme: "from-rose-600 to-pink-600",
    bgTheme: "from-rose-900/20 to-pink-900/20",
    description: "Ask permission from Mother...",
    entryFee: 50,
    questionFee: 25
  },
  "forgive-me-room": {
    name: "Forgive Me For I Have Sinned",
    icon: HelpCircle,
    theme: "from-gray-600 to-slate-700",
    bgTheme: "from-gray-900/20 to-slate-900/20",
    description: "Confess and seek forgiveness...",
    entryFee: 50,
    questionFee: 25
  }
};

const GenericAskRoomPanel = ({ roomId }) => {
  const { token, user } = useContext(AuthContext);
  const [hasPaidEntry, setHasPaidEntry] = useState(false);
  const [question, setQuestion] = useState("");
  const [fchipsBalance, setFchipsBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hostOnline, setHostOnline] = useState(false);
  const [hostUsername, setHostUsername] = useState("");

  const config = ASK_ROOM_CONFIGS[roomId];
  const isAskRoom = !!config;

  // Fetch F-Chips balance
  useEffect(() => {
    if (!isAskRoom) return;
    const fetchBalance = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/fchips/balance`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFchipsBalance(response.data.balance || 0);
      } catch (error) {
        console.error("Failed to fetch balance:", error);
      }
    };
    fetchBalance();
  }, [token, isAskRoom]);

  // Check entry status and host online
  useEffect(() => {
    if (!isAskRoom) return;
    const checkStatus = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/ask-room/${roomId}/entry-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasPaidEntry(response.data.has_paid || false);
        setHostOnline(response.data.host_online || false);
        setHostUsername(response.data.host_username || "Host");
      } catch (error) {
        setHasPaidEntry(false);
      }
    };
    checkStatus();
    const interval = setInterval(checkStatus, 30000);
    return () => clearInterval(interval);
  }, [token, isAskRoom, roomId]);

  if (!isAskRoom) return null;

  const Icon = config.icon;

  const payEntryFee = async () => {
    if (fchipsBalance < config.entryFee) {
      toast.error(`You need ${config.entryFee} F-Chips to enter. You have ${fchipsBalance}.`);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ask-room/${roomId}/pay-entry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasPaidEntry(true);
      setFchipsBalance(response.data.new_balance || fchipsBalance - config.entryFee);
      toast.success(response.data.message || "Entry paid!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  const submitQuestion = async () => {
    if (!question.trim()) {
      toast.error("Please enter a question");
      return;
    }
    if (fchipsBalance < config.questionFee) {
      toast.error(`You need ${config.questionFee} F-Chips. You have ${fchipsBalance}.`);
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/ask-room/${roomId}/submit-question`, 
        { question: question.trim() },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setQuestion("");
      setFchipsBalance(response.data.new_balance || fchipsBalance - config.questionFee);
      toast.success("Question submitted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit question");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`bg-gradient-to-br ${config.bgTheme} rounded-lg border border-gray-700 overflow-hidden mb-3`}>
      {/* Header */}
      <div className={`bg-gradient-to-r ${config.theme} p-3 text-white`}>
        <div className="flex items-center gap-2">
          <Icon className="w-6 h-6" />
          <div>
            <h3 className="font-bold text-lg">{config.name}</h3>
            <p className="text-xs opacity-80">{config.description}</p>
          </div>
        </div>
      </div>

      <div className="p-3">
        {/* Host Status */}
        <div className="flex items-center justify-between mb-3 bg-black/30 rounded-lg p-2">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${hostOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
            <span className="text-sm text-gray-300">
              {hostUsername || "Host"} is {hostOnline ? "ONLINE" : "offline"}
            </span>
          </div>
          <div className="flex items-center gap-1 text-yellow-400">
            <Coins className="w-4 h-4" />
            <span className="font-bold">{fchipsBalance}</span>
          </div>
        </div>

        {!hasPaidEntry ? (
          // Entry Gate
          <div className="text-center">
            <Lock className="w-12 h-12 mx-auto text-gray-400 mb-2" />
            <p className="text-gray-300 mb-3">Pay entry fee to access this room</p>
            <button
              onClick={payEntryFee}
              disabled={loading || fchipsBalance < config.entryFee}
              className={`px-6 py-2 rounded-lg font-bold transition-all ${
                fchipsBalance >= config.entryFee
                  ? `bg-gradient-to-r ${config.theme} text-white hover:opacity-90`
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {loading ? "Processing..." : `Pay ${config.entryFee} F-Chips`}
            </button>
          </div>
        ) : (
          // Question Input
          <div className="space-y-3">
            <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-2 text-center">
              <span className="text-green-400 text-sm">✓ Entry paid for today</span>
            </div>

            <div>
              <label className="text-gray-400 text-sm mb-1 block">Your Question ({config.questionFee} F-Chips)</label>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Type your question here..."
                className="w-full p-2 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm resize-none"
                rows={3}
                disabled={!hostOnline}
              />
            </div>

            <button
              onClick={submitQuestion}
              disabled={submitting || !question.trim() || !hostOnline || fchipsBalance < config.questionFee}
              className={`w-full py-2 rounded-lg font-bold flex items-center justify-center gap-2 transition-all ${
                hostOnline && question.trim() && fchipsBalance >= config.questionFee
                  ? `bg-gradient-to-r ${config.theme} text-white hover:opacity-90`
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Send className="w-4 h-4" />
              {submitting ? "Sending..." : `Send Question (${config.questionFee} F-Chips)`}
            </button>

            {!hostOnline && (
              <p className="text-yellow-500 text-xs text-center">
                ⚠️ {hostUsername || "Host"} is offline - questions will be answered when they return
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GenericAskRoomPanel;
